import React, { useState } from 'react';
import { partners, associates } from '../mock';
import { Linkedin, Mail } from 'lucide-react';

// Rotating brand-styled monogram palette (deep navy + teal)
const monoBg = [
  'linear-gradient(135deg, #17307a 0%, #23499e 100%)',
  'linear-gradient(135deg, #0f7373 0%, #1c9c9c 100%)',
  'linear-gradient(135deg, #17307a 0%, #0f7373 100%)',
  'linear-gradient(135deg, #2b4a92 0%, #1c9c9c 100%)',
];

const initials = (name) =>
  name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

// Avatar that shows a photo when available; falls back to elegant monogram if
// the image URL is empty OR fails to load.
const MonogramAvatar = ({ name, image, idx = 0, isolated = false, className = '' }) => {
  const [broken, setBroken] = useState(false);
  const showImage = !!image && !broken;
  return (
    <div className={`relative w-full h-full ${className}`}>
      {showImage ? (
        isolated ? (
          <div
            className="w-full h-full relative overflow-hidden"
            style={{
              background:
                'linear-gradient(180deg, #f2f5fa 0%, #e6ecf4 55%, #d8e0ec 100%)',
            }}
          >
            {/* decorative floor shadow to ground the subject */}
            <span
              className="absolute bottom-[6%] left-1/2 -translate-x-1/2 w-[70%] h-3 rounded-full bg-black/15 blur-md"
              aria-hidden="true"
            ></span>
            <img
              src={image}
              alt={name}
              onError={() => setBroken(true)}
              className="relative w-full h-full object-cover"
              style={{ objectPosition: 'center 15%', mixBlendMode: 'multiply' }}
            />
          </div>
        ) : (
          <img
            src={image}
            alt={name}
            onError={() => setBroken(true)}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 22%' }}
          />
        )
      ) : (
        <div
          className="w-full h-full flex items-center justify-center relative overflow-hidden"
          style={{ background: monoBg[idx % monoBg.length] }}
          aria-label={name}
        >
          {/* subtle decorative rings */}
          <span
            className="absolute -top-16 -right-16 w-56 h-56 rounded-full border border-white/10"
            aria-hidden="true"
          ></span>
          <span
            className="absolute -bottom-20 -left-20 w-56 h-56 rounded-full border border-white/10"
            aria-hidden="true"
          ></span>
          <span className="font-serif-display text-white/95 text-[3.2rem] tracking-tight italic">
            {initials(name)}
          </span>
        </div>
      )}
    </div>
  );
};

const Team = () => {
  return (
    <section id="team" className="relative py-24 lg:py-36 bg-ivory">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4 mb-6">
              <span className="gold-divider"></span>
              <span className="font-mono-label text-gold-dark">Meet the Experts</span>
            </div>
            <h2 className="font-serif-display text-4xl lg:text-[3.4rem] leading-[1.05] text-ink">
              The minds behind <span className="italic text-gold-dark">every</span> engagement.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="font-sans-body text-lg text-muted-kmh leading-relaxed">
              A senior-led team of Company Secretaries, advisors, and compliance specialists — each chosen for
              the depth of judgement they bring to your boardroom.
            </p>
          </div>
        </div>

        {/* Partners */}
        <p className="font-mono-label text-gold-dark mb-10">— Partners</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
          {partners.map((p, idx) => (
            <div key={p.name} className="group card-lift">
              <div className="img-zoom aspect-[4/5] relative overflow-hidden">
                <MonogramAvatar name={p.name} image={p.image} idx={idx} isolated={p.isolated} />
                <span className="absolute top-4 left-4 font-mono-label text-white/90 bg-black/25 backdrop-blur-sm px-2.5 py-1">
                  {String(idx + 1).padStart(2, '0')}
                </span>
              </div>
              <div className="flex items-end justify-between mt-6 border-t border-line pt-5">
                <div>
                  <h3 className="font-serif-display text-2xl text-ink leading-tight">{p.name}</h3>
                  <p className="font-mono-label text-gold-dark mt-2">{p.role}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button className="w-9 h-9 rounded-full border border-line hover:border-gold flex items-center justify-center">
                    <Linkedin className="w-4 h-4 text-ink-soft" strokeWidth={1.4} />
                  </button>
                  <button className="w-9 h-9 rounded-full border border-line hover:border-gold flex items-center justify-center">
                    <Mail className="w-4 h-4 text-ink-soft" strokeWidth={1.4} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Associates */}
        <p className="font-mono-label text-gold-dark mb-10">— Associates</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-line border border-line">
          {associates.map((a, idx) => (
            <div key={a.name} className="bg-ivory hover:bg-paper group flex flex-col">
              <div className="img-zoom aspect-square relative overflow-hidden">
                <MonogramAvatar name={a.name} image={a.image} idx={idx + 2} />
                <span className="absolute top-3 left-3 font-mono-label text-white/90 bg-black/25 backdrop-blur-sm px-2 py-1">
                  {String(idx + 1).padStart(2, '0')}
                </span>
              </div>
              <div className="p-5 lg:p-6 border-t border-line">
                <p className="font-serif-display text-lg text-ink leading-tight">{a.name}</p>
                <p className="font-mono-label text-gold-dark mt-2">{a.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;
